import { ICartFilterBody } from "@/api_framework/api_modals/Cart";
import type { IEMRListForUserAndPetResponseData } from "@/api_framework/api_modals/EMRs";
import { ICustomerPetListPet } from "@/api_framework/api_modals/Pets";
import { ICustomerDetailsCustomerProfile, IExotelInformationData } from "@/api_framework/api_modals/Users";
import RBACFlag from "@/app/components/core/RBACFlag";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import { useCreateEMRAPI } from "@/app/hooks/api_hooks/emr/useCreateEMRAPI";
import { useV2EMRListAPI } from "@/app/hooks/api_hooks/emr/useV2EMRListAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { ensureMicrophoneAccess } from "@/app/utils/microphoneAccess";
import { isActiveEMRForPet, isVoiceAutoStartUser } from "@/app/utils/voiceAutoStart";
import { useNewCartAPI } from "@/app/hooks/api_hooks/Cart/useNewCartAPI";
import { useCartProfilePinGate } from "@/app/hooks/useCartProfilePinGate";
import CartProfilePinModal from "@/app/components/routes/modals/CartProfilePinModal";
import { useClinicManagerPhoneAPI } from "@/app/hooks/api_hooks/customer_profile/useClinicManagerPhoneAPI";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";
import { useAmeyoExotelManualCallAPI } from "@/app/hooks/api_hooks/ameyo/useAmeyoExotelManualCallAPI";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { ROUTES } from "@/app/routes";
import MappedClinicAndCityPage from "@/app/routes/error_routes/MapClinicAndCityPage";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  setEMRFormFields,
  setEMRMode,
} from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { FaUserPlus } from "react-icons/fa6";
import { BsTruck } from "react-icons/bs";
import { BsMic } from "react-icons/bs";
import { HiOutlineLink } from "react-icons/hi";
import { PiCalculatorThin } from "react-icons/pi";
import { IoArrowForward } from "react-icons/io5";
import { useNavigate } from "react-router";
import { ImPencil } from "react-icons/im";
import { Tooltip } from "react-tooltip";
import PetSelectDropdownComponent from "../PetDropdown";
import AddUserPerSona from "../PhoneDropdown/AddUserPerSona";
import EmiCalculation from "../PhoneDropdown/EmiCalculation";
import PhoneSelectDropdownComponent from "../PhoneDropdown";
import Teleconsultation from "../PhoneDropdown/teleconsultation";
import UserWalletSection from "../UserWalletSection";
import SmartQCDeeplinkModal from "@/app/components/routes/modals/SmartQCDeeplinkModal";
import PetUpdateModal from "@/app/components/ui/Modals/PetUpdateModel";
import PetBirthdayIcon from "@/app/components/ui/Reusable/PetBirthdayIcon";
import CustomerActionToolbar from "./CustomerActionToolbar";

const labelClass = "text-xs text-[#808DA0] font-medium";
const valueClass = "text-xs text-black";
const cellClass = "min-w-0 rounded-md p-0 sm:p-2";

const CustomerInformationDefaultView: React.FC<{
  customerDetails: ICustomerDetailsCustomerProfile;
  customerPets: ICustomerPetListPet[];
  customerMembershipStatus: boolean;
  handleCustomerEdit: () => void;
  hidePetsSection?: boolean;
}> = ({ customerDetails, customerPets, customerMembershipStatus, handleCustomerEdit, hidePetsSection = false }) => {
  const { setVisible } = useModalActions("NO_APP_SEND_REQUEST");
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { unbilledCart, walletDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState
    .state as ICartState;
  const { customerId, petId, petDetails } = routeState.state as ICustomerProfileState;

  const { routeState: slotState } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);
  const { customerBookings } = slotState.state as ISlotsState;

  const mappedClinicID = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId ?? "");
  const mappedClinicName = useAppSelector((state) => state.root?.UIStates?.mappedClinicName);
  const currentUserEmail = useAppSelector((state) => state?.auth?.user?.email);
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const fetchClinicmanagersMobile = useClinicManagerPhoneAPI();
  const { execute: fetchCartList } = useCartListAPI();
  const { execute: createNewCart } = useNewCartAPI();
  const { execute: initManualCall } = useAmeyoExotelManualCallAPI();
  const [open, setOpen] = useState(false);
  const [emiCalculation, setEmiCalculation] = useState(false);
  const [isVisibleMappedClinic, setIsVisibleMappedClinic] = useState<boolean>(false);
  const [persona, setPersona] = useState(false);
  const [openSmartQCDeeplink, setOpenSmartQCDeeplink] = useState<boolean>(false);
  const [isPetUpdateModalOpen, setIsPetUpdateModalOpen] = useState<boolean>(false);
  const { runWithPinGate, isCheckingEligibility, pinModalProps } = useCartProfilePinGate();
  const [selectedPhone, setSelectedPhone] = useState(customerDetails?.mobile);
  const [loader, setLoader] = useState(false);
  const isPetFetchInProgress = useLoader("isPetListFetchInProgress");
  const isCustomerDetailsFetchInProgress = useLoader("isCustomerDetailsFetchInProgress");
  const today = new Date().toISOString().split("T")[0];
  const isBookingPresent = customerBookings?.data?.filter(
    (b) => b.booking_start_date === today && !["Cancelled", "Failed"].includes(b.booking_status),
  );
  // RBAC: CART_CREATE_WITHOUT_CLINIC_MAP — legacy role: central_ops
  const isCentralOpps = useFeatureAccess("CART_CREATE_WITHOUT_CLINIC_MAP");
  // RBAC: CART_CREATE_FROM_CLINIC — legacy roles: clinic_ops, clinic_ops_doggyworld
  const isClinicOpps = useFeatureAccess("CART_CREATE_FROM_CLINIC");

  const canUseVoiceToEMR =
    useFeatureAccess("VOICE_TO_EMR_BUTTON") && isVoiceAutoStartUser(currentUserEmail);

  const userEmail = useAppSelector((state) => state?.auth?.user?.email);
  // `id`, not `user_id` — IOAuthUser has no `user_id` field (see VoiceEMRRecorder).
  const userId = useAppSelector((state) => state?.auth?.userLoginInfo?.user_id);
  const { execute: clickLogAPI } = useClickLogAPI();

  // Mirrors VoiceEMRRecorder's logVoiceEMRClick so events from the launch button
  // and the recorder panel land on the same VOICE_TO_EMR screen with the same shape.
  const logVoiceEMRClick = (
    event_type: string,
    cta_text: string,
    extra: Partial<IClickLogBody> = {},
  ) => {
    clickLogAPI({
      event_type,
      cta_text,
      scr_id: "VOICE_TO_EMR",
      timestamp: Date.now(),
      user_id: userId,
      vms_user_id: userEmail,
      client_id: customerId,
      pet_id: petId,
      clinic_id: mappedClinicID,
      ...extra,
    });
  };

  const dispatch = useAppDispatch();
  const { execute: createDraftEMR } = useCreateEMRAPI();
  const { execute: fetchEmrList } = useV2EMRListAPI();
  const [isStartingVoiceEMR, setIsStartingVoiceEMR] = useState(false);
  // Inline rather than a toast: a doctor who steps away should still find out why
  // nothing happened when they come back.
  const [voiceEMRError, setVoiceEMRError] = useState<string | null>(null);

  // Creates a draft EMR for this pet and opens it in the SAME tab with the voice
  // panel already recording — the doctor goes from profile to talking in one tap.
  // Same tab so the floating voice recorder isn't stranded in another window.
  const openVoiceEMRTab = (emrId: string) =>
    navigate(`/app/customer/profile/create-emr?emr_id=${emrId}&emr_status=DRAFT&voice=1`);

  const findOpenDraftEMR = async (
    forCustomerId: string,
    forPetId: string,
  ): Promise<{ lookupSucceeded: boolean; draft: IEMRListForUserAndPetResponseData | null }> => {
    const emrList = await fetchEmrList(forCustomerId, forPetId);
    if (!Array.isArray(emrList)) return { lookupSucceeded: false, draft: null };

    const draftsForPet = emrList
      .filter((emr) => isActiveEMRForPet(emr, forPetId))
      .sort((left, right) =>
        (right?.updated_at ?? right?.created_at ?? 0) - (left?.updated_at ?? left?.created_at ?? 0),
      );

    return { lookupSucceeded: true, draft: draftsForPet[0] ?? null };
  };

  const handleStartVoiceEMR = async () => {
    if (!customerId || !petId || isStartingVoiceEMR) return;

    setIsStartingVoiceEMR(true);
    setVoiceEMRError(null);
    logVoiceEMRClick("VOICE_EMR_BUTTON_CLICKED", "Voice to EMR");
    try {
      // Settle microphone permission here, on the page the doctor is looking at,
      // so a blocked microphone is known before an EMR is created for it.
      const allowed = await ensureMicrophoneAccess();
      if (!allowed) {
        logVoiceEMRClick("VOICE_EMR_MIC_PERMISSION_DENIED", "Microphone permission denied");
        setVoiceEMRError("Microphone blocked. Allow microphone access in your browser, then try again.");
        return;
      }

      // Profile Redux draft state can belong to the previously selected pet. The
      // API lookup is customer+pet scoped, and a failed lookup must never fall
      // through to creation because that could produce another active draft.
      const { lookupSucceeded, draft: openDraft } = await findOpenDraftEMR(
        customerId,
        petId,
      );
      if (!lookupSucceeded) {
        setVoiceEMRError("Couldn't check existing EMR drafts. Please try again.");
        return;
      }

      // Same VMS user already owns the active draft → reopen it directly with
      // voice recording. A draft owned by a *different* user no longer blocks
      // with the "Clear Your EMR Drafts" modal — we fall through and create a
      // fresh EMR for this user below.
      if (openDraft?.emr_id && currentUserEmail && openDraft.created_by === currentUserEmail) {
        openVoiceEMRTab(openDraft.emr_id);
        return;
      }

      dispatch(setEMRMode("CREATE"));
      dispatch(setEMRFormFields({ formFields: null }));
      const response = await createDraftEMR(customerId, petId);
      if (response?.emr_id) {
        openVoiceEMRTab(response.emr_id);
      } else {
        setVoiceEMRError("Couldn't create the EMR. Please try again.");
      }
    } finally {
      setIsStartingVoiceEMR(false);
    }
  };

  const isMappedClinicPresent = isBookingPresent?.some((booking) => booking?.clinic_id === mappedClinicID);


  const handelCartCreationFunction = (clinic_id?: any, clinic_name?: any) => {
    setLoader(false);

    createNewCart(
      customerId,
      clinic_id ? clinic_id : mappedClinicID,
      clinic_name ? clinic_name : mappedClinicName,
      petId,
    ).then(({ cart_id }) => {
      setIsVisibleMappedClinic(false);
      if (cart_id) {
        setLoader(false);
        window.open(`/app/user-cart/cart-creation/${customerId}/${cart_id}?from_cart=true&&pet_id=${petId}`, "_blank");
      }
    });
  };

  // A pet's DOB is unusable for cart/billing when it was never captured ("null pet
  // age") or when it lands in the future ("negative DOB" — age would come out
  // negative). In either case we block the cart hop and ask the user to fix it first.
  const isPetDobInvalid = (dob?: string | null): boolean => {
    if (!dob) return true;
    // Parse "YYYY-MM-DD" as a LOCAL date (not UTC via new Date(str)) so a pet born
    // today isn't wrongly read as future-dated in +offset timezones like IST.
    const [year, month, day] = dob.split("-").map(Number);
    if (!year || !month || !day) return true;
    const dobDate = new Date(year, month - 1, day);
    if (isNaN(dobDate.getTime())) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dobDate.getTime() > today.getTime();
  };

  const proceedToCart = () => {
    if (isMappedClinicPresent || isCentralOpps) {
      runWithPinGate(
        {
          userId: customerId,
          clinicId: mappedClinicID,
          userMobileNumber: customerDetails?.mobile,
          petId,
          source: "CUSTOMER_PROFILE",
        },
        handelCartCreationFunction,
      );
    } else {
      setIsVisibleMappedClinic(true);
    }
    localStorage.setItem("selectedPetId", petId);
  };

  const handelRedirectToCart = () => {
    if (isPetDobInvalid(petDetails?.dob)) {
      setIsPetUpdateModalOpen(true);
      return;
    }
    proceedToCart();
  };

  const handleHdCartClick = async () => {
    window.open(`/app/user-cart/hd-cart/${customerId}?from_cart=true`, "_self");
  };
  const handleOpenManualCRTPanel = async () => {
    if (!customerId) return;
    const manualCallResponse = await initManualCall();
    const crtId = manualCallResponse?.crt_id;
    if (!crtId) return;
    const exotelSessionPayload: IExotelInformationData = {
      event: "incoming_call",
      customer_pet_data: {
        customer_id: customerId || null,
        pet_id: petId || null,
      },
      rawPhoneNumber: selectedPhone || customerDetails?.mobile || null,
      CRT: crtId,
    };
    sessionStorage.setItem(`exotel_information:${crtId}`, JSON.stringify(exotelSessionPayload));
    sessionStorage.setItem("exotel_information_current", JSON.stringify(exotelSessionPayload));

    const refreshStamp = Date.now();
    const queryParams = new URLSearchParams({
      isExotel: "true",
      control_panel: "true",
      CRT: crtId,
      CRT_ID: crtId,
      customer_id: customerId,
      refresh: `${refreshStamp}`,
    });
    if (petId) {
      queryParams.set("pet_id", petId);
    }
    if (selectedPhone) {
      queryParams.set("phone_number", selectedPhone);
    }
    const targetPath = petId
      ? `/app/customer/profile/customer-details/appointments/${customerId}/${petId}`
      : `/app/customer/profile/customer-details/appointments/${customerId}`;
    navigate(`${targetPath}?${queryParams.toString()}`);
  };

  useEffect(() => {
    if (customerId) {
      fetchUserWalletDetails(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) {
      const filterBody: ICartFilterBody = {
        cart_status: ["Unbilled"],
        start_date: null,
        end_date: null,
      };
      fetchCartList(filterBody, customerId);
    }
  }, []);

  const handelProfileVisit = () => {
    navigate(`/app/customer/profile/customer-details/appointments/${customerId}/${petId}`);
  };

  const handelInvisibleMapped = () => {
    setIsVisibleMappedClinic(false);
    setLoader(false);
  };

  return (
    <div className="">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 sm:px-2">
          <p className="text-xs sm:text-sm font-semibold">
            Customer Profile
          </p>
          <PetBirthdayIcon hasBirthday={petDetails?.has_birthday} petName={petDetails?.name} />
        </div>
        <CustomerActionToolbar
          customerDetails={customerDetails}
          customerId={customerId}
          hidePetsSection={hidePetsSection}
          onCallClick={() => {
            setOpen(true);
            fetchClinicmanagersMobile();
          }}
          onEmiClick={() => setEmiCalculation(true)}
          onPersonaClick={() => setPersona(true)}
          onSmartQCDeeplinkClick={() => setOpenSmartQCDeeplink(true)}
          onOpenControlPanelClick={handleOpenManualCRTPanel}
          canUseVoiceToEMR={canUseVoiceToEMR}
          voiceToEMRLoading={isStartingVoiceEMR}
          voiceToEMRDisabled={isStartingVoiceEMR || !petId}
          voiceToEMRError={voiceEMRError}
          onVoiceToEMRClick={handleStartVoiceEMR}
        />
      </div>

      <div className=" w-full">

        <div className="flex gap-3 sm:gap-2 flex-wrap ">
          <div className={`${cellClass} `}>
            <div className="flex w-full items-start justify-between gap-3">

              <h3 className="flex flex-wrap flex-col  gap-1 text-left text-xs">
                <span
                  className="cursor-pointer  font-medium hyper-color whitespace-nowrap "
                  onClick={handelProfileVisit}
                >
                  {startCase(customerDetails?.name)}
                </span>
                {customerDetails?.customer_code && !isClinicOpps && (
                  <span className="flex items-center text-xs font-normal text-[#808da0] ">
                    ({customerDetails?.customer_code})
                    <ClipboardComponent
                      className="ml-1 text-xs "
                      tooltipText={`Copy ${customerDetails?.customer_code}`}
                      text={`${customerDetails?.customer_code}`}
                      copyId={`copy-user-code:${customerDetails?.email}`}
                    />
                  </span>
                )}
              </h3>


              {!hidePetsSection && (
                <RBACFlag actionId="EDIT_CUSTOMER">
                  <button
                    onClick={handleCustomerEdit}
                    className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-neutral-100"
                  >
                    <ImPencil className="h-3 w-3" />
                  </button>
                </RBACFlag>
              )}
            </div>
          </div>

          {!hidePetsSection && (
            <div className={cellClass}>
              <p className={labelClass}>{`Pets(${customerPets?.length}):`}</p>
              <div className={valueClass}>
                <PetSelectDropdownComponent pets={customerPets} />
              </div>
            </div>
          )}

          <div className={cellClass}>
            <p className={labelClass}>Phone:</p>
            <div className={valueClass}>
              <PhoneSelectDropdownComponent
                setSelectedPhone={setSelectedPhone}
                phoneNumbers={{
                  alternate: customerDetails?.alternate_number,
                  mobile: customerDetails?.mobile,
                  isMobileWhatsapp: customerDetails?.is_whatsapp_mobile,
                  isAlternateWhatsapp: customerDetails?.is_whatsapp_alternate,
                }}
              />
            </div>
          </div>

          <div className={cellClass}>
            <p className={labelClass}>Membership:</p>
            <p className={`${valueClass} whitespace-nowrap `}>
              {customerMembershipStatus ? "Member" : "Non-Member"}
            </p>
          </div>

          <div className={cellClass}>
            <p className={labelClass}>Customer:</p>
            <p className={`${valueClass} whitespace-nowrap`}>{customerDetails?.customer_type}</p>
          </div>

          <div className={cellClass}>
            <p className={labelClass}>NPS:</p>
            <p className={valueClass}>{customerDetails?.nps}</p>
          </div>

          <div className={cellClass}>
            <p className={labelClass}>App:</p>
            {customerDetails?.app_downloaded ? (
              <p className={valueClass}>Yes</p>
            ) : (
              <p> <button
                data-tooltip-content="Send App Request"
                data-tooltip-id="send_app_request"
                className="cursor-pointer text-xs font-normal text-red-500 underline"
                onClick={setVisible}
              >
                No
              </button>
              </p>
            )}
          </div>

          <div className={cellClass}>
            <p className={labelClass}>Account:</p>
            <div className="flex items-center gap-2">
              <p
                className={`text-xs ${walletDetails?.wallet_amount - walletDetails?.overdue_amount < 0 ? "text-red-600" : "text-black"}`}
              >
                {(walletDetails?.wallet_amount - walletDetails?.overdue_amount)?.toFixed(2)}
              </p>
              <UserWalletSection />
            </div>
          </div>

          <div className={`${cellClass} `}>
            <div className="flex gap-2">
              {isPetFetchInProgress || isCustomerDetailsFetchInProgress ? (
                <div className="relative text-left rtl:text-right">
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="inline h-5 w-5 animate-spin fill-blue-600 text-gray-200"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                petDetails && (
                  <>
                    <RBACFlag actionId="CART_CREATION_ACTION_CTA">
                      {loader || isVisibleMappedClinic || isCheckingEligibility ? (
                        <div className="relative p-1 text-left rtl:text-right ">
                          <div role="status">
                            <svg
                              aria-hidden="true"
                              className="inline h-5 w-5 animate-spin fill-blue-600 text-gray-200"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"
                              />
                            </svg>
                            <span className="sr-only">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div
                            className="group flex cursor-pointer items-center rounded-lg  px-3 py-2"
                            onClick={handelRedirectToCart}
                          >
                            <span className={`relative flex items-center `}>
                              <ShoppingCartIcon className="h-5 w-5 text-gray-600 transition duration-300 group-hover:text-blue-500 lg:h-6 lg:w-6" />
                              <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                {unbilledCart}
                              </span>
                            </span>
                          </div>

                        </div>
                      )}
                    </RBACFlag>
                    {canUseVoiceToEMR && (
                      <div className="hidden sm:flex">
                        <button
                          type="button"
                          disabled={isStartingVoiceEMR || !petId}
                          className="flex h-8 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-[12px] font-medium leading-none text-white hover:bg-indigo-700 focus:bg-indigo-700 active:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => void handleStartVoiceEMR()}
                        >
                          <BsMic className="h-4 w-4 text-white" />
                          <span className="whitespace-nowrap text-[12px]">
                            {isStartingVoiceEMR ? "Starting..." : "Voice to EMR"}
                          </span>
                        </button>
                        {voiceEMRError && (
                          <p className="ml-2 max-w-[220px] self-center text-[11px] leading-snug text-red-600">
                            {voiceEMRError}
                          </p>
                        )}
                      </div>
                    )}
                    <RBACFlag actionId="HD_CART_CTA">
                      <div className="hidden sm:flex">
                        <button
                          type="button"
                          className="flex h-8 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-black bg-black px-4 py-2 text-[12px] font-medium leading-none text-white hover:bg-black focus:bg-black active:bg-black"
                          onClick={handleHdCartClick}
                        >
                          <BsTruck className="h-5 w-5 text-white" />
                          <span className="whitespace-nowrap text-[12px]">HD Cart</span>
                        </button>
                      </div>
                    </RBACFlag>

                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {open && <Teleconsultation setOpen={setOpen} selected={selectedPhone} customerId={customerId} />}
      {persona && <AddUserPerSona setOpen={setPersona} selected={customerDetails?.name} customerId={customerId} />}
      {emiCalculation && <EmiCalculation setOpen={setEmiCalculation} isOpen={emiCalculation} />}
      {openSmartQCDeeplink && (
        <SmartQCDeeplinkModal
          open={openSmartQCDeeplink}
          setOpen={setOpenSmartQCDeeplink}
          customerId={customerId}
          customerMobile={customerDetails?.mobile}
          customerName={customerDetails?.name}
        />
      )}

      <CartProfilePinModal {...pinModalProps} />
      <PetUpdateModal
        isOpen={isPetUpdateModalOpen}
        errorSource={null}
        onClose={() => setIsPetUpdateModalOpen(false)}
        onSuccess={() => {
          setIsPetUpdateModalOpen(false);
          // DOB has been fixed via the form; skip the guard and continue to cart.
          proceedToCart();
        }}
      />

      {
        isVisibleMappedClinic && (
          <MappedClinicAndCityPage
            isCrossVisible={true}
            message="Please verify your mapped clinic. No bookings were found for this user today."
            isCartEmrCreate={true}
            setIsVisibleMappedClinic={() => handelInvisibleMapped()}
            handelCartEmrCreate={(id, name) => handelCartCreationFunction(id, name)}
          />
        )
      }

      <Tooltip
        id="missing_details_customer"
        render={({ content }) => {
          return (
            <div>
              <p className="text-theme-sm">The pet has missing details</p>
              <p className="mt-2 text-theme-sm">
                {content?.split(",").map((key) => {
                  return <span className="block">{key}</span>;
                }) ?? ""}
              </p>
            </div>
          );
        }}
      />
    </div >
  );
};

export default CustomerInformationDefaultView;
